import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sigma, FileUp, Loader2, Trash2, ShieldCheck, ShieldAlert, AlertTriangle, GraduationCap, ChevronDown, HelpCircle, CheckCircle2, Circle } from "lucide-react";

const ALGO_LABEL = {
  sum: "Somme", sum_1c: "Somme (compl. à 1)", sum_2c: "Somme (compl. à 2)",
  xor: "XOR", crc16_ccitt: "CRC16-CCITT", crc16_xmodem: "CRC16-XMODEM", crc32: "CRC32",
};

const DIMSPORT_STEPS = [
  { t: "Ouvrez le fichier ORIGINAL dans Dimsport Race Evo", d: "Lisez l'ECU au banc/OBD puis chargez le fichier stock (l'ORI intact)." },
  { t: "Corrompez volontairement le checksum → fichier AVANT", d: "Modifiez 1 map ou l'octet du checksum et exportez SANS correction. C'est le fichier « AVANT » (checksum KO)." },
  { t: "Relancez Dimsport avec correction checksum → fichier APRÈS", d: "Rechargez le même fichier, laissez Dimsport recalculer/corriger le checksum, puis exportez. C'est le fichier « APRÈS » (corrigé)." },
  { t: "Envoyez la paire ci-dessous, par famille ECU", d: "AVANT + APRÈS doivent avoir EXACTEMENT la même taille. Le moteur repère l'octet du checksum et l'algorithme." },
  { t: "Ajoutez une 2ᵉ paire pour valider (≥ 2 paires concordantes)", d: "Idéalement 2 véhicules différents de la même famille. À la validation → livraison 100% auto des combinaisons." },
];

const StatusBadge = ({ r }) => {
  if (r.validated) return <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-success/15 text-signal-success flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Validé (auto)</span>;
  if (r.matched_fields > 0 && r.matched_fields === r.total_fields) return <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-processing/15 text-signal-processing flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Appris · +1 paire pour valider</span>;
  if (r.matched_fields > 0) return <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-processing/15 text-signal-processing">Partiel {r.matched_fields}/{r.total_fields}</span>;
  return <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-error/15 text-signal-error flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Non identifié</span>;
};

// Progress toward 100% auto-delivery: 2 concordant pairs = validated
const FamilyProgress = ({ r }) => {
  const need = 2;
  const done = r.validated ? need : Math.min(r.samples_count || 0, need);
  const pct = Math.round((done / need) * 100);
  const nextHint = r.validated
    ? "Livraison automatique active pour cette famille ✅"
    : (r.matched_fields > 0 && r.matched_fields === r.total_fields)
      ? "Algorithme trouvé — envoyez 1 paire de plus (autre véhicule) pour verrouiller l'auto-livraison."
      : r.matched_fields > 0
        ? "Algorithme partiellement identifié — envoyez d'autres paires de la même famille."
        : "Non identifié sur cet échantillon — checksum probablement multi-blocs/complexe. Envoyez d'autres paires ; sinon le workflow reste assisté (tuner).";
  return (
    <div className="w-full mt-2" data-testid={`checksum-progress-${r.family}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${r.validated ? "bg-signal-success" : "bg-aurora-cyan"}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="font-mono text-[9px] text-zinc-500 shrink-0">{done}/{need} paires</span>
      </div>
      <p className={`font-mono text-[10px] leading-snug ${r.validated ? "text-signal-success" : "text-zinc-400"}`}>{nextHint}</p>
    </div>
  );
};

export const ChecksumLearning = () => {
  const [family, setFamily] = useState("");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const bRef = useRef(null);
  const aRef = useRef(null);

  const load = () => api.get("/admin/checksum/rules").then(({ data }) => setRules(data.rules || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const validatedCount = rules.filter((r) => r.validated).length;

  const learn = async () => {
    if (!family.trim()) return toast.error("Indiquez la famille ECU (ex. Bosch EDC17)");
    if (!before || !after) return toast.error("Ajoutez le fichier AVANT (checksum KO) et APRÈS (corrigé)");
    setBusy(true);
    const fd = new FormData();
    fd.append("family", family.trim());
    fd.append("before_file", before);
    fd.append("after_file", after);
    try {
      const { data } = await api.post("/admin/checksum/learn", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.validated) toast.success(`✅ ${data.family} : algorithme validé — livraison auto activée`);
      else if (data.matched_fields === data.total_fields && data.total_fields > 0) toast.success(`Algorithme appris (${data.samples_count} paire) — ajoutez 1 paire de plus pour valider`);
      else toast.warning(`Checksum non identifié automatiquement (${data.notes}). Envoyez d'autres paires ou l'algo est trop complexe.`);
      setBefore(null); setAfter(null);
      if (bRef.current) bRef.current.value = "";
      if (aRef.current) aRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'apprentissage");
    } finally { setBusy(false); }
  };

  const del = async (fam) => {
    if (!window.confirm(`Supprimer l'apprentissage et les échantillons de « ${fam} » ?`)) return;
    try { await api.delete(`/admin/checksum/family/${encodeURIComponent(fam)}`); toast.success("Réinitialisé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="checksum-learning">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Apprentissage checksum</h3>
        {rules.length > 0 && (
          <span className="ml-auto font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-success/10 text-signal-success" data-testid="checksum-validated-count">
            {validatedCount} famille{validatedCount > 1 ? "s" : ""} en auto
          </span>
        )}
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
        Fournissez, par famille ECU, une paire de fichiers : <b className="text-white">AVANT</b> (checksum incorrect) et <b className="text-signal-success">APRÈS</b> (corrigé par <b>Dimsport Race Evo</b>). À partir de <b className="text-aurora-cyan">2 paires concordantes</b>, la correction devient automatique → livraison 100% auto des combinaisons.
      </p>

      {/* Guide repliable */}
      <button
        data-testid="checksum-guide-toggle"
        onClick={() => setGuideOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 mb-4 rounded-sm border border-aurora-cyan/25 bg-aurora-cyan/5 hover:bg-aurora-cyan/10 transition-colors text-left"
      >
        <HelpCircle className="w-4 h-4 text-aurora-cyan shrink-0" />
        <span className="font-display text-xs uppercase tracking-wide text-white">Comment produire les paires avec Dimsport ?</span>
        <ChevronDown className={`w-4 h-4 text-aurora-cyan ml-auto transition-transform ${guideOpen ? "rotate-180" : ""}`} />
      </button>
      {guideOpen && (
        <ol className="space-y-3 mb-5 pl-1" data-testid="checksum-guide">
          {DIMSPORT_STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 shrink-0 grid place-items-center rounded-full bg-aurora-cyan/15 text-aurora-cyan font-mono text-[11px] font-bold">{i + 1}</span>
              <div>
                <div className="text-sm text-white leading-snug">{s.t}</div>
                <div className="font-mono text-[10px] text-zinc-500 leading-snug mt-0.5">{s.d}</div>
              </div>
            </li>
          ))}
          <li className="flex gap-3 pt-1 border-t border-white/5">
            <span className="w-6 h-6 shrink-0 grid place-items-center rounded-full bg-signal-processing/15 text-signal-processing"><AlertTriangle className="w-3.5 h-3.5" /></span>
            <div className="font-mono text-[10px] text-zinc-400 leading-snug">
              Si le checksum est <b className="text-white">multi-blocs</b> ou à seed propriétaire, il peut rester « non identifié » : le workflow reste alors <b>assisté</b> (le tuner valide dans Dimsport en ~30 s). Aucune livraison aveugle n'est jamais faite.
            </div>
          </li>
        </ol>
      )}

      <div className="mb-4">
        <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Famille ECU</label>
        <input data-testid="checksum-family" className={inputCls} placeholder="Bosch EDC17C64" value={family} onChange={(e) => setFamily(e.target.value)} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <input ref={bRef} type="file" className="hidden" data-testid="checksum-before-input" onChange={(e) => setBefore(e.target.files[0])} />
          <button onClick={() => bRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-signal-error/40 hover:border-signal-error transition-colors">
            <FileUp className="w-5 h-5 text-signal-error" />
            <span className="font-mono text-xs text-zinc-300 truncate">{before ? before.name : "Fichier AVANT (checksum KO)"}</span>
          </button>
        </div>
        <div>
          <input ref={aRef} type="file" className="hidden" data-testid="checksum-after-input" onChange={(e) => setAfter(e.target.files[0])} />
          <button onClick={() => aRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-signal-success/40 hover:border-signal-success transition-colors">
            <FileUp className="w-5 h-5 text-signal-success" />
            <span className="font-mono text-xs text-zinc-300 truncate">{after ? after.name : "Fichier APRÈS (corrigé Dimsport)"}</span>
          </button>
        </div>
      </div>
      <button data-testid="checksum-learn-btn" onClick={learn} disabled={busy} className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sigma className="w-4 h-4" />} Apprendre l'algorithme
      </button>

      {rules.length > 0 && (
        <div className="mt-6 space-y-3" data-testid="checksum-rules">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Algorithmes enregistrés · progression vers l'auto-livraison</div>
          {rules.map((r) => (
            <div key={r.family} data-testid={`checksum-rule-${r.family}`} className="border border-white/10 rounded-sm px-4 py-3 bg-black/20">
              <div className="flex items-center gap-3 flex-wrap">
                {r.validated ? <CheckCircle2 className="w-4 h-4 text-signal-success shrink-0" /> : <Circle className="w-4 h-4 text-zinc-600 shrink-0" />}
                <span className="font-display text-sm font-bold text-white">{r.family}</span>
                <StatusBadge r={r} />
                <span className="font-mono text-[10px] text-zinc-500">{r.samples_count} paire{r.samples_count > 1 ? "s" : ""}</span>
                {(r.rules || []).map((x, i) => (
                  <span key={i} className="font-mono text-[10px] text-aurora-cyan">{ALGO_LABEL[x.algo] || x.algo} · {x.width}o @0x{x.offset.toString(16).toUpperCase()} · [{x.range_start}→{x.range_end}]</span>
                ))}
                <button data-testid={`checksum-del-${r.family}`} onClick={() => del(r.family)} className="ml-auto w-8 h-8 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
              <FamilyProgress r={r} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
